import React from 'react';
import { TEXT_COLOR } from '../../theme';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

const LABEL_SX = {
    display: 'block',
    fontSize: '11px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.07em',
    color: TEXT_COLOR,
    mb: 0.75,
};

// Mismos niveles para la etiqueta principal y las alternativas; el backend
// valida contra la misma lista (ALTERNATIVE_LEVELS en serializers/reply.py).
const LEVEL_OPTIONS = [
    { value: 2, text: 'operative', roman: 'II' },
    { value: 3, text: 'tactic', roman: 'III' },
    { value: 4, text: 'strategic', roman: 'IV' },
    { value: 5, text: 'strategic', roman: 'V' },
    { value: 6, text: 'strategic', roman: 'VI' },
];
const SUBLEVEL_OPTIONS = ['A', 'B', 'C'];

function FieldLabel({ children }) {
    return <Typography sx={LABEL_SX} component="div">{children}</Typography>;
}

function LevelSelect({ tr, value, onChange, name, inputProps, error }) {
    return (
        <FormControl fullWidth size="small" error={error}>
            <Select name={name} value={value} onChange={onChange} inputProps={inputProps}>
                <MenuItem value=""><em>—</em></MenuItem>
                {LEVEL_OPTIONS.map(o => (
                    <MenuItem key={o.value} value={o.value}>{tr(o.text)} ({o.roman})</MenuItem>
                ))}
            </Select>
        </FormControl>
    );
}

function SublevelSelect({ value, onChange, name, inputProps, error }) {
    return (
        <FormControl fullWidth size="small" error={error}>
            <Select name={name} value={value} onChange={onChange} inputProps={inputProps}>
                <MenuItem value=""><em>—</em></MenuItem>
                {SUBLEVEL_OPTIONS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </Select>
        </FormControl>
    );
}

function AlternativeLabels({ tr, hasMain, alternatives, errors, onAdd, onChange, onRemove }) {
    return (
        <Box sx={{ mt: 3 }}>
            <FieldLabel>{tr('alternative_labels')}</FieldLabel>
            <Typography sx={{ fontSize: '13px', color: 'text.secondary', mb: 1.5 }}>
                {tr('alternative_labels_help')}
            </Typography>

            {alternatives.map((alt, index) => {
                const error = errors[index];
                return (
                    <Box
                        key={index}
                        data-testid="alternative-row"
                        sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto 2fr', gap: 2, alignItems: 'center', mb: 1 }}
                    >
                        <LevelSelect
                            tr={tr}
                            value={alt.level}
                            error={Boolean(error)}
                            inputProps={{ 'aria-label': `${tr('level')} ${index + 1}` }}
                            onChange={e => onChange(index, 'level', e.target.value)}
                        />
                        <SublevelSelect
                            value={alt.sublevel}
                            error={Boolean(error)}
                            inputProps={{ 'aria-label': `${tr('sub_level')} ${index + 1}` }}
                            onChange={e => onChange(index, 'sublevel', e.target.value)}
                        />
                        <IconButton size="small" aria-label={tr('remove_alternative')} onClick={() => onRemove(index)}>
                            <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                        <FormHelperText error sx={{ m: 0 }}>{error ? tr(error) : ''}</FormHelperText>
                    </Box>
                );
            })}

            <Button size="small" startIcon={<AddIcon />} onClick={onAdd} disabled={!hasMain}>
                {tr('add_alternative')}
            </Button>
        </Box>
    );
}

export default function LabelingCard({
    tr, nivelLabel, subnivelLabel, invalid, published, handleChange, handleToggle,
    alternativeLabels = [], alternativeErrors = [], addAlternative, changeAlternative, removeAlternative,
}) {
    const hasMain = nivelLabel !== '' && subnivelLabel !== '';
    return (
        <Box sx={{
            bgcolor: '#fff',
            border: '1px solid #E1E4E8',
            borderRadius: '14px',
            boxShadow: '0 1px 2px rgba(15,17,35,0.04)',
            p: 3,
        }}>
            <Typography sx={{ fontSize: '16px', fontWeight: 700, color: TEXT_COLOR, mb: 2.5 }}>
                {tr('labeled')}
            </Typography>

            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2, alignItems: 'end' }}>
                <Box>
                    <FieldLabel>{tr('level')}</FieldLabel>
                    <LevelSelect tr={tr} name="nivelLabel" value={nivelLabel} onChange={handleChange} />
                </Box>

                <Box>
                    <FieldLabel>{tr('sub_level')}</FieldLabel>
                    <SublevelSelect name="subnivelLabel" value={subnivelLabel} onChange={handleChange} />
                </Box>

                <Box>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={invalid}
                                onChange={handleToggle('invalid')}
                                size="small"
                            />
                        }
                        label={
                            <Typography sx={{ fontSize: '14px', fontWeight: 500, color: TEXT_COLOR }}>
                                {tr('invalid')}
                            </Typography>
                        }
                    />
                </Box>

                <Box>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={published}
                                onChange={handleToggle('published')}
                                size="small"
                            />
                        }
                        label={
                            <Typography sx={{ fontSize: '14px', fontWeight: 500, color: TEXT_COLOR }}>
                                {tr('published')}
                            </Typography>
                        }
                    />
                </Box>
            </Box>

            <AlternativeLabels
                tr={tr}
                hasMain={hasMain}
                alternatives={alternativeLabels}
                errors={alternativeErrors}
                onAdd={addAlternative}
                onChange={changeAlternative}
                onRemove={removeAlternative}
            />
        </Box>
    );
}
